import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider, createRootRoute, createRoute, Outlet } from '@tanstack/react-router';
import { useAuth } from '@/stores/auth';
import { LoginPage } from '@/routes/login';
import { VenuesListPage } from '@/routes/venues';
import { VenueDetailPage } from '@/routes/venues/$id';
import { UsersListPage } from '@/routes/users';
import { UserDetailPage } from '@/routes/users/$id';
import { ActivitiesListPage } from '@/routes/activities';
import { ActivityDetailPage } from '@/routes/activities/$id';
import { OrgsListPage } from '@/routes/orgs';
import { OrgDetailPage } from '@/routes/orgs/$id';
import { AuditListPage } from '@/routes/audit';
import { ReportsPage } from '@/routes/reports';
import { ToolsPage } from '@/routes/tools';
import { DashboardPage } from '@/routes/dashboard';
import { AdminShell } from '@/components/AdminShell';
import { Toaster } from '@/components/ui/sonner';

function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuth((s) => s.status);
  const me = useAuth((s) => s.me);
  const bootstrap = useAuth((s) => s.bootstrap);

  useEffect(() => { bootstrap(); }, []);

  if (status === 'loading') return <div className="p-8 text-zinc-400">Загрузка…</div>;
  if (!me) return <LoginPage onSuccess={() => bootstrap()} />;
  return <>{children}</>;
}

const rootRoute = createRootRoute({
  component: () => (
    <AuthGate>
      <AdminShell>
        <Outlet />
      </AdminShell>
    </AuthGate>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsPage,
});

const venuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/venues',
  component: VenuesListPage,
});

const venueDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/venues/$id',
  component: VenueDetailPage,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: UsersListPage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$id',
  component: UserDetailPage,
});

const activitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activities',
  component: ActivitiesListPage,
});

const activityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activities/$id',
  component: ActivityDetailPage,
});

const orgsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orgs',
  component: OrgsListPage,
});

const orgDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orgs/$id',
  component: OrgDetailPage,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditListPage,
});

const toolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tools',
  component: ToolsPage,
});

const router = createRouter({
  routeTree: rootRoute.addChildren([
    indexRoute, venuesRoute, venueDetailRoute, usersRoute, userDetailRoute,
    activitiesRoute, activityDetailRoute, orgsRoute, orgDetailRoute,
    auditRoute, toolsRoute, reportsRoute,
  ]),
});
declare module '@tanstack/react-router' {
  interface Register { router: typeof router; }
}

const queryClient = new QueryClient();

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  );
}
